/**
 * Owner: Masa Bayu
 * File: demo-cart-store.ts
 * Date:Time: 2026-09-15 22:25
 * Description: T3 cart/order/payment ledger store. §466 S3 — katalog dan harga
 *   datang daripada qxp-hub (tiada carta harga rekaan); pembayaran dilejarayakan
 *   atas akaun sesi hub yang sebenar, bukan akaun demo.
 */

import {
  beginPaymentForOrder,
  createCheckoutFromCart,
  createEmptyCart,
  createEmptyPaymentLedger,
  createPendingOrderDraft,
  createQxreadId,
  createQxreadTelemetryEvent,
  reconcilePaymentCallback,
  removeFromCart,
  retryPaymentForOrder,
  revalidateCart,
  serializeCart,
  updateCartLineQuantity,
  addToCart,
  type QxreadCart,
  type QxreadCheckoutSession,
  type QxreadConfirmedOrder,
  type QxreadEntitlementIssuanceBatch,
  type QxreadOrderDraft,
  type QxreadPaymentAttempt,
  type QxreadPaymentCallbackStatus,
  type QxreadPaymentLedger,
  type QxreadTelemetryEvent,
} from '@qx/qxread-core';
import { getHubAuthAccountId } from '../publication/hub-auth';
import { listCatalog } from '../publication/catalog-port';
import { findCatalogOffer, listCatalogOffers } from './catalog-offers';

function sessionKey(): string {
  return `qxrdsess_${getHubAuthAccountId() ?? 'anonymous'}`;
}

function paymentAccountId(): string {
  const accountId = getHubAuthAccountId();
  if (accountId === null) {
    throw new Error('Payment ledger requires a signed-in hub account');
  }
  return accountId;
}

let cart: QxreadCart = createEmptyCart({ sessionKey: sessionKey() });
let lastCheckout: QxreadCheckoutSession | null = null;
let lastOrder: QxreadOrderDraft | null = null;
let confirmedOrder: QxreadConfirmedOrder | null = null;
let paymentAttempt: QxreadPaymentAttempt | null = null;
let entitlementBatch: QxreadEntitlementIssuanceBatch | null = null;
let paymentLedger: QxreadPaymentLedger = createEmptyPaymentLedger();
let lastTelemetry: QxreadTelemetryEvent | null = null;

function recordTelemetry(
  name:
    | 'qxread.commerce.payment_intent'
    | 'qxread.commerce.payment_attempt'
    | 'qxread.commerce.payment_callback'
    | 'qxread.commerce.order_confirmed'
    | 'qxread.commerce.entitlement_issued',
  attributes: Readonly<Record<string, string | number | boolean | null>>,
): void {
  lastTelemetry = createQxreadTelemetryEvent({
    name,
    correlationId: lastOrder?.orderId ?? 'qxrdord_unassigned',
    attributes,
  });
}

export function getDemoCart(): QxreadCart {
  cart = revalidateCart(cart, listCatalog(), listCatalogOffers());
  return cart;
}

export function addDemoOfferToCart(publicationId: string): QxreadCart {
  const offer = findCatalogOffer(publicationId);
  if (offer === null) {
    throw new Error('No hub offer for this publication (catalog entry or server price missing)');
  }
  cart = addToCart(getDemoCart(), offer, listCatalog(), 1);
  lastCheckout = null;
  lastOrder = null;
  confirmedOrder = null;
  paymentAttempt = null;
  entitlementBatch = null;
  paymentLedger = createEmptyPaymentLedger();
  return cart;
}

export function updateDemoCartQuantity(publicationId: string, quantity: number): QxreadCart {
  cart = updateCartLineQuantity(getDemoCart(), publicationId, quantity);
  lastCheckout = null;
  lastOrder = null;
  confirmedOrder = null;
  paymentAttempt = null;
  entitlementBatch = null;
  paymentLedger = createEmptyPaymentLedger();
  return cart;
}

export function removeDemoCartLine(publicationId: string): QxreadCart {
  cart = removeFromCart(getDemoCart(), publicationId);
  lastCheckout = null;
  lastOrder = null;
  confirmedOrder = null;
  paymentAttempt = null;
  entitlementBatch = null;
  paymentLedger = createEmptyPaymentLedger();
  return cart;
}

export function prepareDemoCheckout(): QxreadCheckoutSession {
  lastCheckout = createCheckoutFromCart(getDemoCart());
  lastOrder = null;
  confirmedOrder = null;
  paymentAttempt = null;
  entitlementBatch = null;
  paymentLedger = createEmptyPaymentLedger();
  return lastCheckout;
}

export function createDemoPendingOrder(): QxreadOrderDraft {
  const checkout = lastCheckout ?? prepareDemoCheckout();
  if (checkout.state !== 'ready' || checkout.totals === null) {
    throw new Error(checkout.blockers.join('; ') || 'Checkout blocked');
  }
  const current = getDemoCart();
  lastOrder = createPendingOrderDraft(
    checkout,
    current.lines.map((line) => ({
      publicationId: line.publicationId,
      title: line.snapshot.title,
      quantity: line.quantity,
      sellerPriceMinorUnits: line.snapshot.sellerPriceMinorUnits,
      currency: line.snapshot.currency,
    })),
  );
  confirmedOrder = null;
  paymentAttempt = null;
  entitlementBatch = null;
  paymentLedger = createEmptyPaymentLedger();
  return lastOrder;
}

export function beginDemoPayment(): {
  readonly order: QxreadOrderDraft;
  readonly attempt: QxreadPaymentAttempt;
  readonly reused: boolean;
} {
  if (lastOrder === null || lastOrder.state !== 'pending') {
    throw new Error('Demo payment requires a pending order');
  }
  if (confirmedOrder !== null) {
    throw new Error('Demo payment blocked — order already confirmed');
  }
  const idempotencyKey =
    paymentAttempt !== null && paymentAttempt.state === 'processing'
      ? paymentAttempt.idempotencyKey
      : createQxreadId('qxrdpayk');
  const started = beginPaymentForOrder(lastOrder, paymentLedger, { idempotencyKey });
  lastOrder = started.order;
  paymentAttempt = started.attempt;
  paymentLedger = started.ledger;
  recordTelemetry('qxread.commerce.payment_intent', {
    intentId: started.intent.intentId,
    orderId: started.order.orderId,
    reused: started.reused,
  });
  recordTelemetry('qxread.commerce.payment_attempt', {
    attemptId: started.attempt.attemptId,
    state: started.attempt.state,
    reused: started.reused,
  });
  return Object.freeze({
    order: started.order,
    attempt: started.attempt,
    reused: started.reused,
  });
}

export function applyDemoPaymentOutcome(
  status: Exclude<QxreadPaymentCallbackStatus, 'processing'>,
): {
  readonly attempt: QxreadPaymentAttempt;
  readonly order: QxreadOrderDraft | QxreadConfirmedOrder;
  readonly entitlementBatch: QxreadEntitlementIssuanceBatch | null;
  readonly duplicateCallback: boolean;
} {
  if (lastOrder === null || paymentAttempt === null) {
    throw new Error('Demo payment outcome requires an active attempt');
  }
  if (lastOrder.state !== 'pending') {
    throw new Error('Demo payment outcome requires a pending order');
  }
  const callbackId = createQxreadId('qxrdpayc');
  const result = reconcilePaymentCallback({
    order: lastOrder,
    attempt: paymentAttempt,
    callback: {
      callbackId,
      attemptId: paymentAttempt.attemptId,
      status,
      providerReference: `local_${status}`,
      reason: status === 'succeeded' ? null : `local_${status}`,
    },
    ledger: paymentLedger,
    accountId: paymentAccountId(),
  });
  paymentAttempt = result.attempt;
  paymentLedger = result.ledger;
  entitlementBatch = result.entitlementBatch;
  recordTelemetry('qxread.commerce.payment_callback', {
    callbackId,
    status,
    duplicate: result.duplicateCallback,
    attemptState: result.attempt.state,
  });

  if (result.order.state === 'confirmed') {
    confirmedOrder = result.order;
    recordTelemetry('qxread.commerce.order_confirmed', {
      orderId: result.order.orderId,
      paymentAttemptId: result.order.paymentAttemptId,
    });
    if (result.entitlementBatch !== null) {
      recordTelemetry('qxread.commerce.entitlement_issued', {
        batchId: result.entitlementBatch.batchId,
        count: result.entitlementBatch.entitlements.length,
        opensLibrary: false,
        launchesReader: false,
      });
    }
  } else {
    lastOrder = result.order;
    if (result.order.state === 'cancelled') {
      confirmedOrder = null;
    }
  }

  return Object.freeze({
    attempt: result.attempt,
    order: result.order,
    entitlementBatch: result.entitlementBatch,
    duplicateCallback: result.duplicateCallback,
  });
}

/** Replay the last succeeded callback id path is covered in core; UI uses a fresh callback. */
export function replayDemoSucceededCallback(): {
  readonly duplicateCallback: boolean;
  readonly entitlementCount: number;
} {
  if (paymentAttempt === null || paymentAttempt.state !== 'succeeded' || lastOrder === null) {
    throw new Error('Demo duplicate callback requires a succeeded attempt');
  }
  const pendingShape: QxreadOrderDraft = Object.freeze({
    orderId: lastOrder.orderId,
    checkoutId: lastOrder.checkoutId,
    cartId: lastOrder.cartId,
    state: 'pending',
    lines: lastOrder.lines,
    totals: lastOrder.totals,
    createdAt: lastOrder.createdAt,
    paymentAttemptId: paymentAttempt.attemptId,
    entitlementId: null,
  });
  const result = reconcilePaymentCallback({
    order: pendingShape,
    attempt: paymentAttempt,
    callback: {
      callbackId: createQxreadId('qxrdpayc'),
      attemptId: paymentAttempt.attemptId,
      status: 'succeeded',
      providerReference: 'local_succeeded_replay',
    },
    ledger: paymentLedger,
    accountId: paymentAccountId(),
  });
  paymentLedger = result.ledger;
  entitlementBatch = result.entitlementBatch;
  if (result.order.state === 'confirmed') {
    confirmedOrder = result.order;
  }
  return Object.freeze({
    duplicateCallback: result.duplicateCallback,
    entitlementCount: result.entitlementBatch?.entitlements.length ?? 0,
  });
}

export function retryDemoPaymentAfterFailure(): {
  readonly order: QxreadOrderDraft;
  readonly attempt: QxreadPaymentAttempt;
} {
  if (lastOrder === null || paymentAttempt === null || paymentAttempt.state !== 'failed') {
    throw new Error('Demo payment retry requires a failed attempt on a pending order');
  }
  const started = retryPaymentForOrder(lastOrder, paymentAttempt, paymentLedger, {
    idempotencyKey: createQxreadId('qxrdpayk'),
  });
  lastOrder = started.order;
  paymentAttempt = started.attempt;
  paymentLedger = started.ledger;
  entitlementBatch = null;
  confirmedOrder = null;
  recordTelemetry('qxread.commerce.payment_attempt', {
    attemptId: started.attempt.attemptId,
    state: started.attempt.state,
    retry: true,
  });
  return Object.freeze({ order: started.order, attempt: started.attempt });
}

export function getDemoCheckout(): QxreadCheckoutSession | null {
  return lastCheckout;
}

export function getDemoOrder(): QxreadOrderDraft | null {
  return lastOrder;
}

export function getDemoConfirmedOrder(): QxreadConfirmedOrder | null {
  return confirmedOrder;
}

export function getDemoPaymentAttempt(): QxreadPaymentAttempt | null {
  return paymentAttempt;
}

export function getDemoEntitlementBatch(): QxreadEntitlementIssuanceBatch | null {
  return entitlementBatch;
}

export function getDemoPaymentTelemetry(): QxreadTelemetryEvent | null {
  return lastTelemetry;
}

export function persistDemoCartPayload() {
  return serializeCart(getDemoCart());
}

/** Test helper — reset module state. */
export function resetDemoCartStore(): void {
  cart = createEmptyCart({ sessionKey: sessionKey() });
  lastCheckout = null;
  lastOrder = null;
  confirmedOrder = null;
  paymentAttempt = null;
  entitlementBatch = null;
  paymentLedger = createEmptyPaymentLedger();
  lastTelemetry = null;
}
